import { useCallback, useRef } from 'react';
import { useToast } from '@/hooks/use-toast';
import { checkClientRateLimit } from '@/lib/validation';

interface RateLimitConfig {
  action: string;
  maxActions: number;
  windowMs: number;
  showToast?: boolean;
  toastTitle?: string;
  toastDescription?: string;
}

interface UseRateLimitReturn {
  /** Check if action is allowed. Returns true if within limits, false if rate limited. */
  checkLimit: () => boolean;
  /** Execute a callback only if rate limit allows. Returns the callback result or undefined if blocked. */
  withRateLimit: <T>(callback: () => T | Promise<T>) => Promise<T | undefined>;
  /** Check if currently rate limited without recording an action */
  isLimited: () => boolean;
  /** Debounce check - prevents rapid successive calls (doesn't count against rate limit) */
  canProceed: () => boolean;
}

/**
 * Hook for client-side rate limiting with toast feedback
 * Prevents UI spam and improves UX during API calls
 * 
 * @example
 * const { withRateLimit } = useRateLimit({
 *   action: 'auth_attempt',
 *   maxActions: 5,
 *   windowMs: 60000, // 1 minute
 * });
 * 
 * const handleLogin = () => withRateLimit(async () => {
 *   await signIn(email, password);
 * });
 */
export function useRateLimit(config: RateLimitConfig): UseRateLimitReturn {
  const { toast } = useToast();
  const lastActionRef = useRef<number>(0);
  const DEBOUNCE_MS = 300;

  const {
    action,
    maxActions,
    windowMs,
    showToast = true,
    toastTitle = "Slow down!",
    toastDescription,
  } = config;

  const getDefaultDescription = useCallback(() => {
    const windowSeconds = Math.ceil(windowMs / 1000);
    const windowMinutes = Math.ceil(windowMs / 60000);
    const timeUnit = windowMs >= 60000 ? `${windowMinutes} minute${windowMinutes > 1 ? 's' : ''}` : `${windowSeconds} second${windowSeconds > 1 ? 's' : ''}`;
    return `You can only do this ${maxActions} time${maxActions > 1 ? 's' : ''} per ${timeUnit}. Please wait.`;
  }, [maxActions, windowMs]);

  const checkLimit = useCallback((): boolean => {
    const allowed = checkClientRateLimit(action, maxActions, windowMs);
    
    if (!allowed && showToast) {
      toast({
        title: toastTitle,
        description: toastDescription || getDefaultDescription(),
        variant: "destructive",
      });
    }
    
    return allowed;
  }, [action, maxActions, windowMs, showToast, toast, toastTitle, toastDescription, getDefaultDescription]);

  const isLimited = useCallback((): boolean => {
    // Check without recording - peek at current state
    const key = `rate_limit_${action}`;
    const now = Date.now();
    const windowStart = now - windowMs;
    
    const stored = localStorage.getItem(key);
    if (!stored) return false;
    
    const actions: number[] = JSON.parse(stored);
    const validActions = actions.filter(timestamp => timestamp > windowStart);
    
    return validActions.length >= maxActions;
  }, [action, maxActions, windowMs]);

  const canProceed = useCallback((): boolean => {
    const now = Date.now();
    if (now - lastActionRef.current < DEBOUNCE_MS) {
      return false;
    }
    lastActionRef.current = now;
    return true;
  }, []);

  const withRateLimit = useCallback(async <T>(callback: () => T | Promise<T>): Promise<T | undefined> => {
    // First check debounce
    if (!canProceed()) {
      return undefined;
    }
    
    // Then check rate limit
    if (!checkLimit()) {
      return undefined;
    }
    
    return await callback();
  }, [canProceed, checkLimit]);

  return {
    checkLimit,
    withRateLimit,
    isLimited,
    canProceed,
  };
}

// Preset configurations for common use cases
export const RATE_LIMIT_PRESETS = {
  auth: {
    action: 'auth_attempt',
    maxActions: 5,
    windowMs: 60 * 1000, // 5 attempts per minute
    toastTitle: "Too many attempts",
    toastDescription: "Please wait a minute before trying again.",
  },
  export: {
    action: 'export_request',
    maxActions: 3,
    windowMs: 5 * 60 * 1000, // 3 exports per 5 minutes
    toastTitle: "Export limit reached",
    toastDescription: "Please wait a few minutes before exporting again.",
  },
  batchUpload: {
    action: 'batch_upload',
    maxActions: 5,
    windowMs: 10 * 60 * 1000, // 5 batch uploads per 10 minutes
    toastTitle: "Upload limit reached",
    toastDescription: "You've uploaded a lot recently. Please wait before uploading more.",
  },
  deleteReceipt: {
    action: 'delete_receipt',
    maxActions: 20,
    windowMs: 60 * 1000, // 20 deletes per minute
    toastTitle: "Slow down",
    toastDescription: "You're deleting too fast. Please wait a moment.",
  },
  guestChat: {
    action: 'guest_chat',
    maxActions: 5,
    windowMs: 5 * 60 * 1000, // 5 messages per 5 minutes for guests
    toastTitle: "Demo limit reached",
    toastDescription: "Sign up for free to continue chatting!",
  },
  chat: {
    action: 'chat_message',
    maxActions: 5,
    windowMs: 60 * 1000, // 5 messages per minute
    toastTitle: "Slow down",
    toastDescription: "You're chatting too fast. Take a breath and try again.",
  },
  receiptUpload: {
    action: 'receipt_upload',
    maxActions: 10,
    windowMs: 60 * 1000, // 10 uploads per minute
    toastTitle: "Upload limit reached",
    toastDescription: "You're uploading too fast. Please wait a moment.",
  },
  generalAction: {
    action: 'general_action',
    maxActions: 30,
    windowMs: 60 * 1000, // 30 actions per minute
  },
} as const;
